import React, { useState, useEffect } from 'react';
import { Layout, Typography, Card, Space, message, Spin, Tabs, Button } from 'antd';
import { DashboardOutlined, DatabaseOutlined, BarChartOutlined, ReloadOutlined, LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import ExecutionDashboard from './components/ExecutionDashboard';
import CreateExecution from './components/CreateExecution';
import LogModal from './components/LogModal';
import Statistics from './components/Statistics';
import Login from './components/Login';
import Config from './components/Config';
import Profile from './components/Profile';
import { supabase } from './lib/supabase';
import './styles.css';

const { Header, Content } = Layout;
const { Title } = Typography;

const API_URL = process.env.REACT_APP_API_URL || '';


function App() {
  const [executions, setExecutions] = useState([]);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [serverStatus, setServerStatus] = useState('disconnected');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  
  // Sorting and filtering state
  const [sorting, setSorting] = useState({
    field: 'created_at',
    order: 'desc'
  });
  
  const [filters, setFilters] = useState({
    status: 'all'
  });

  // Check auth state on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user || null);
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user || null);
        setAuthLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Fetch server status
  const fetchServerStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/status`);
      const data = await response.json();
      setServerStatus(data.status);
    } catch (error) {
      setServerStatus('disconnected');
      console.error('Failed to fetch server status:', error);
    }
  };

  // Handle login success
  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      message.success('Çıkış yapıldı');
    } catch (error) {
      message.error('Çıkış yapılırken hata oluştu');
      console.error('Logout error:', error);
    }
  };

  // Fetch executions with pagination
  const fetchExecutions = async (newPagination = pagination, newSorting = sorting, newFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: newPagination.page.toString(),
        pageSize: newPagination.pageSize.toString(),
        sortField: newSorting.field,
        sortOrder: newSorting.order,
        status: newFilters.status
      });

      const response = await fetch(`${API_URL}/api/executions?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setExecutions(data.executions || []);
        setPagination(data.pagination);
        setSorting(data.sorting);
        setFilters(data.filters);
      } else {
        throw new Error(data.message || 'Failed to fetch executions');
      }
    } catch (error) {
      console.error('Failed to fetch executions:', error);
      message.error('Failed to fetch executions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh function
  const handleRefresh = async () => {
    message.loading({ content: 'Refreshing executions...', key: 'refresh', duration: 1 });
    await fetchExecutions();
    message.success({ content: 'Executions refreshed!', key: 'refresh', duration: 2 });
  };

  // Fix inconsistent statuses
  const handleFixStatuses = async () => {
    try {
      message.loading({ content: 'Fixing status inconsistencies...', key: 'fix', duration: 2 });
      
      const response = await fetch(`${API_URL}/api/executions/fix-statuses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success({ 
          content: `Fixed ${data.updates.successToCompleted} inconsistent statuses`, 
          key: 'fix',
          duration: 3
        });
        // Refresh executions after fix
        await fetchExecutions();
      } else {
        throw new Error(data.message || 'Failed to fix statuses');
      }
    } catch (error) {
      console.error('Failed to fix statuses:', error);
      message.error({ 
        content: 'Failed to fix statuses: ' + error.message, 
        key: 'fix' 
      });
    }
  };

  // Create a new execution
  const createExecution = async (executionData) => {
    try {
      // Add current user ID to execution data
      const executionWithUser = {
        ...executionData,
        runned_by: user?.id // Include current user ID
      };

      const response = await fetch(`${API_URL}/api/executions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(executionWithUser)
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('Execution created successfully!');
        fetchExecutions(); // Refresh the list
        return data.execution;
      } else {
        throw new Error(data.message || 'Failed to create execution');
      }
    } catch (error) {
      console.error('Failed to create execution:', error);
      message.error('Failed to create execution: ' + error.message);
      return null;
    }
  };

  // Start execution
  const startExecution = async (executionId) => {
    try {
      const response = await fetch(`${API_URL}/api/executions/${executionId}/start`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success !== false) {
        message.success('Execution started directly!');
        fetchExecutions(); // Refresh the list
        return true;
      } else {
        throw new Error(data.message || 'Failed to start execution');
      }
    } catch (error) {
      console.error('Failed to start execution:', error);
      message.error('Failed to start execution: ' + error.message);
      return false;
    }
  };

  // Start multiple executions
  const startAllExecutions = async (executionIds) => {
    try {
      message.loading({ content: `Starting ${executionIds.length} executions...`, key: 'startAll', duration: 2 });
      
      const response = await fetch(`${API_URL}/api/executions/start-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ executionIds })
      });
      
      const data = await response.json();
      
      if (data.success) {
        const { summary } = data;
        message.success({ 
          content: `Started ${summary.queued}/${summary.total} executions successfully!`,
          key: 'startAll',
          duration: 3
        });
        
        if (summary.failed > 0) {
          message.warning(`${summary.failed} executions failed to start`);
        }
        
        fetchExecutions(); // Refresh the list
        return true;
      } else {
        throw new Error(data.message || 'Failed to start executions');
      }
    } catch (error) {
      console.error('Failed to start executions:', error);
      message.error({ 
        content: 'Failed to start executions: ' + error.message,
        key: 'startAll'
      });
      return false;
    }
  };

  // Stop execution
  const stopExecution = async (executionId) => {
    try {
      const response = await fetch(`${API_URL}/api/executions/${executionId}/stop`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('Execution stopped successfully!');
        fetchExecutions(); // Refresh the list
        return true;
      } else {
        throw new Error(data.message || 'Failed to stop execution');
      }
    } catch (error) {
      console.error('Failed to stop execution:', error);
      message.error('Failed to stop execution: ' + error.message);
      return false;
    }
  };

  // Show logs for an execution
  const showLogs = (reqNumber) => {
    setSelectedExecution(reqNumber);
    setShowLogModal(true);
  };

  // Update execution
  const updateExecution = async (executionId, executionData) => {
    try {
      const response = await fetch(`${API_URL}/api/executions/${executionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(executionData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('Execution updated successfully!');
        fetchExecutions(); // Refresh the list
        return data.execution;
      } else {
        throw new Error(data.message || 'Failed to update execution');
      }
    } catch (error) {
      console.error('Failed to update execution:', error);
      message.error('Failed to update execution: ' + error.message);
      throw error;
    }
  };

  // Delete execution
  const deleteExecution = async (executionId) => {
    try {
      const response = await fetch(`${API_URL}/api/executions/${executionId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('Execution deleted successfully!');
        fetchExecutions(); // Refresh the list
        return true;
      } else {
        throw new Error(data.message || 'Failed to delete execution');
      }
    } catch (error) {
      console.error('Failed to delete execution:', error);
      message.error('Failed to delete execution: ' + error.message);
      throw error;
    }
  };

  // Handle pagination changes
  const handleTableChange = (paginationConfig, filters, sorter) => {
    const newPagination = {
      ...pagination,
      page: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    };

    const newSorting = sorter.field ? {
      field: sorter.field,
      order: sorter.order === 'ascend' ? 'asc' : 'desc'
    } : sorting;

    const newFilters = {
      status: filters.status?.[0] || 'all'
    };

    fetchExecutions(newPagination, newSorting, newFilters);
  };

  useEffect(() => {
    fetchServerStatus();
    fetchExecutions();

    // Set up polling for updates
    const interval = setInterval(() => {
      fetchServerStatus();
      fetchExecutions();
    }, 40000); // Poll every 40 seconds

    return () => clearInterval(interval);
  }, []);

  // Show loading spinner while checking auth
  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f2f5'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Title level={3} style={{ margin: 0, color: '#000' }}>
          Pradea Agent Dashboard
        </Title>
        <Space>
          <span>Welcome, {user.email}</span>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Space>
      </Header>
      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="large"
            items={[
              {
                key: 'dashboard',
                label: (
                  <Space>
                    <DashboardOutlined />
                    Dashboard
                  </Space>
                ),
                children: (
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    
                    {/* Create Execution Card */}
                    <Card 
                      title={
                        <Space>
                          <DatabaseOutlined />
                          Create New Execution
                        </Space>
                      }
                      size="default"
                    >
                      <CreateExecution onCreateExecution={createExecution} />
                    </Card>

                    {/* Executions Dashboard */}
                    <Card 
                      title={
                        <Space>
                          <DashboardOutlined />
                          Execution Dashboard
                          {loading && <Spin size="small" />}
                        </Space>
                      }
                      extra={
                        <Button 
                          icon={<ReloadOutlined />}
                          onClick={handleRefresh}
                          loading={loading}
                          type="text"
                          size="small"
                        >
                          Refresh
                        </Button>
                      }
                    >
                      <ExecutionDashboard
                        executions={executions}
                        onStartExecution={startExecution}
                        onStopExecution={stopExecution}
                        onStartAll={startAllExecutions}
                        onShowLogs={showLogs}
                        onRefresh={fetchExecutions}
                        onUpdateExecution={updateExecution}
                        onDeleteExecution={deleteExecution}
                        onTableChange={handleTableChange}
                        loading={loading}
                        pagination={pagination}
                        sorting={sorting}
                        filters={filters}
                      />
                    </Card>

                  </Space>
                )
              },
              {
                key: 'statistics',
                label: (
                  <Space>
                    <BarChartOutlined />
                    Statistics
                  </Space>
                ),
                children: <Statistics />
              },
              {
                key: 'profile',
                label: (
                  <Space>
                    <UserOutlined />
                    Profil
                  </Space>
                ),
                children: <Profile user={user} />
              },
              {
                key: 'config',
                label: (
                  <Space>
                    <SettingOutlined />
                    Configuration
                  </Space>
                ),
                children: <Config />
              }
            ]}
          />
        </div>
      </Content>

      {/* Log Modal */}
      {showLogModal && (
        <LogModal
          executionId={selectedExecution}
          onClose={() => setShowLogModal(false)}
        />
      )}
    </Layout>
  );
}

export default App;